import { EventQueue } from './queue/event-queue';
import { IEventEmitter, createEventEmitter } from './event-emitter';
import { TrackingEvent, TrackingEventType, EventPayloadMap, EventSchemaMap, EventPriority } from './types/events';
import { TrackingProvider } from './types/providers';
import {
  TrackingValidationError,
  TrackingProviderInitError,
  TrackingProviderNotFoundError,
  TrackingQueueFullError,
  TrackingUnknownEventTypeError,
  TrackingStorageError,
} from './types/errors';
import { TrackingMetadata } from './types/metadata';

export interface TrackingManagerConfig {
  /** Maximum number of events to store in the queue */
  queueMaxSize?: number;

  /** Key to use for local storage */
  queueStorageKey?: string;

  /** Interval in milliseconds to auto-save to local storage */
  queueAutoSaveInterval?: number | null;

  /** Delay in milliseconds between processing events */
  period?: number;
}

const PERIOD: number = 100; // 100ms delay between processing events

type TrackingEventMap = {
  trackingEventReceived: TrackingEvent;
  trackingEventProcessed: TrackingEvent;
};

export class TrackingManager {
  private readonly eventEmitter: IEventEmitter<TrackingEventMap>;
  private readonly queue: EventQueue;
  private readonly providers: Map<string, TrackingProvider> = new Map();
  private processingQueue = false;
  private metadata: TrackingMetadata = {};
  private readonly period: number;

  constructor(config: TrackingManagerConfig = {}) {
    this.eventEmitter = createEventEmitter<TrackingEventMap>();
    this.queue = new EventQueue(config?.queueMaxSize, config?.queueStorageKey, config?.queueAutoSaveInterval);
    this.period = config?.period || PERIOD;
    this.initialize();
  }

  public registerProvider(provider: TrackingProvider): void {
    if (this.providers.has(provider.name)) {
      console.warn(`Provider ${provider.name} is already registered`);
      return;
    }

    this.providers.set(provider.name, provider);
    if (provider.initialize) {
      try {
        provider.initialize();
      } catch (error) {
        throw new TrackingProviderInitError(
          provider.name,
          error instanceof Error ? error.message : 'Unknown error',
          error instanceof Error ? error : undefined
        );
      }
    }
  }

  public unregisterProvider(providerName: string): void {
    const provider = this.providers.get(providerName);
    if (!provider) {
      throw new TrackingProviderNotFoundError(providerName);
    }

    if (provider.destroy) {
      try {
        provider.destroy();
      } catch (error) {
        console.error(`Error destroying provider ${providerName}:`, error);
      }
    }

    this.providers.delete(providerName);
  }

  public addMetadata(metadata: Partial<TrackingMetadata>): void {
    this.metadata = { ...this.metadata, ...metadata };
  }

  public track<T extends TrackingEventType>(
    type: T,
    payload?: EventPayloadMap[T],
    priority: EventPriority = EventPriority.INFO
  ): void {
    // Validate event type
    if (!EventSchemaMap[type]) {
      const error = new TrackingUnknownEventTypeError(type);
      this.error(error);
      return;
    }

    // Validate payload against schema
    const schema = EventSchemaMap[type];
    const validationResult = schema.safeParse(payload);

    if (!validationResult.success) {
      const error = new TrackingValidationError(`Validation failed for event type '${type}'`, validationResult.error);
      this.error(error);
      return;
    }

    const event: TrackingEvent = {
      type,
      timestamp: Date.now(),
      priority,
      payload: validationResult.data,
    };

    // Emit received event
    this.eventEmitter.emit('trackingEventReceived', event);

    // Queue event for providers
    try {
      this.queue.enqueue(event);
    } catch (error) {
      if (error instanceof TrackingQueueFullError) {
        console.warn('Event queue is full, dropping lowest priority events');
      } else if (error instanceof TrackingStorageError) {
        console.error('Failed to save event to local storage:', error);
      } else {
        this.error(error instanceof Error ? error : new Error('Unknown error while enqueueing event'));
      }
    }
  }

  // Log-level based tracking aliases with overloads for optional payloads
  public error(error: Error): void {
    this.track('Error', error, EventPriority.ERROR);
  }

  public warn<T extends TrackingEventType>(name: T): void;
  public warn<T extends TrackingEventType>(name: T, payload: EventPayloadMap[T]): void;
  public warn<T extends TrackingEventType>(name: T, payload?: EventPayloadMap[T]): void {
    this.track(name, payload, EventPriority.WARN);
  }

  public info<T extends TrackingEventType>(name: T): void;
  public info<T extends TrackingEventType>(name: T, payload: EventPayloadMap[T]): void;
  public info<T extends TrackingEventType>(name: T, payload?: EventPayloadMap[T]): void {
    this.track(name, payload, EventPriority.INFO);
  }

  public debug<T extends TrackingEventType>(name: T): void;
  public debug<T extends TrackingEventType>(name: T, payload: EventPayloadMap[T]): void;
  public debug<T extends TrackingEventType>(name: T, payload?: EventPayloadMap[T]): void {
    this.track(name, payload, EventPriority.DEBUG);
  }

  public trace<T extends TrackingEventType>(name: T): void;
  public trace<T extends TrackingEventType>(name: T, payload: EventPayloadMap[T]): void;
  public trace<T extends TrackingEventType>(name: T, payload?: EventPayloadMap[T]): void {
    this.track(name, payload, EventPriority.TRACE);
  }

  // Event listener methods
  public onEventReceived(callback: (event: TrackingEvent) => void): () => void {
    this.eventEmitter.on('trackingEventReceived', callback);
    return () => this.eventEmitter.off('trackingEventReceived', callback);
  }

  public offEventReceived(callback: (event: TrackingEvent) => void): void {
    this.eventEmitter.off('trackingEventReceived', callback);
  }

  public onEventProcessed(callback: (event: TrackingEvent) => void): () => void {
    this.eventEmitter.on('trackingEventProcessed', callback);
    return () => this.eventEmitter.off('trackingEventProcessed', callback);
  }
  public offEventProcessed(callback: (event: TrackingEvent) => void): void {
    this.eventEmitter.off('trackingEventProcessed', callback);
  }

  public saveQueue(): void {
    this.queue.save();
  }

  public restoreQueue(): void {
    this.queue.restore();
  }

  public setQueueAutoSaveInterval(interval: number): void {
    this.queue.setAutoSaveInterval(interval);
  }

  public stopQueueAutoSaving(): void {
    this.queue.stopAutoSaving();
  }

  public initialize(): void {
    if (this.processingQueue) return;

    this.processingQueue = true;

    (async function runQueue(self: TrackingManager) {
      while (self.processingQueue) {
        const event = self.queue.dequeue();
        if (event) {
          await self.processEvent(event);
        }
        await new Promise((resolve) => setTimeout(resolve, self.period));
      }
    })(this);
  }

  private async processEvent(event: TrackingEvent): Promise<void> {
    for (const provider of this.providers.values()) {
      try {
        await provider.trackEvent(event, this.metadata);
      } catch (error) {
        console.error(`Error processing event in provider ${provider.name}:`, error);
      }
    }
    this.eventEmitter.emit('trackingEventProcessed', event);
  }

  public destroy(): void {
    this.processingQueue = false;
    this.stopQueueAutoSaving();

    // Clean up providers
    for (const provider of this.providers.values()) {
      if (provider.destroy) {
        try {
          provider.destroy();
        } catch (error) {
          console.error(`Error destroying provider ${provider.name}:`, error);
        }
      }
    }
    this.providers.clear();
  }

  /**
   * @internal
   * @returns The event queue as an immutable proxy
   */
  public __getQueue(): EventQueue {
    return new Proxy(this.queue, {
      get: (target, prop) => {
        return target[prop as keyof EventQueue];
      },
      set: () => {
        console.warn('Direct modification of queue is not allowed');
        return false;
      },
      deleteProperty: () => {
        console.warn('Direct modification of queue is not allowed');
        return false;
      },
    });
  }
}
